import { z } from 'zod';
import { ApiClient } from '@/common/api/ApiClient.ts';
import {
  billingAddressSchema,
  type BillingAddress,
  type CreateBillingAddressRequest,
  type UpdateBillingAddressRequest,
} from './schemas.ts';

let nextBillingAddressId = 3;

// Standalone mock stores, keyed by partner ID (null = personal).
export const mockUserBillingAddresses: BillingAddress[] = [];
export const mockOrgBillingAddresses: BillingAddress[] = [
  {
    id: 1,
    partner: 1,
    authorizedRole: 'full',
    name: 'Tech Solutions s.r.o.',
    street: 'Václavské náměstí 1',
    city: 'Prague',
    zipCode: '110 00',
    country: 'CZ',
    VAT_ID: 'CZ12345678',
    CRN: '12345678',
    email: 'billing@techsol.cz',
  },
  {
    id: 2,
    partner: 2,
    authorizedRole: 'full',
    name: 'Green Energy Partners',
    street: 'Náměstí Svobody 10',
    city: 'Brno',
    zipCode: '602 00',
    country: 'CZ',
    VAT_ID: null,
    CRN: '87654321',
    email: 'finance@greenep.eu',
  },
];

function findMockAddress(id: number): { address: BillingAddress; collection: BillingAddress[] } {
  const orgAddr = mockOrgBillingAddresses.find((a) => a.id === id);
  if (orgAddr) return { address: orgAddr, collection: mockOrgBillingAddresses };
  const userAddr = mockUserBillingAddresses.find((a) => a.id === id);
  if (userAddr) return { address: userAddr, collection: mockUserBillingAddresses };
  throw new Error(`Billing address ${id} not found`);
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const BillingAddressService = {
  /**
   * Returns all billing addresses accessible to the current user — personal
   * (partner = null) and org addresses — each with an `authorizedRole` field
   * indicating the user's permission level for that address.
   */
  getList: async () => {
    return ApiClient.get('/billing/contact/', z.array(billingAddressSchema), undefined, {
      mock: () => [
        ...mockUserBillingAddresses.map((a) => ({
          ...a,
          partner: null,
          authorizedRole: 'full' as const,
        })),
        ...mockOrgBillingAddresses,
      ],
    });
  },

  create: async (data: CreateBillingAddressRequest) => {
    return ApiClient.post('/billing/contact/', data, billingAddressSchema, {
      mock: () => {
        const newAddr: BillingAddress = {
          ...data,
          id: nextBillingAddressId++,
          partner: data.partner ?? null,
          authorizedRole: 'full',
        };
        if (data.partner) {
          mockOrgBillingAddresses.push(newAddr);
        } else {
          mockUserBillingAddresses.push(newAddr);
        }
        return { ...newAddr };
      },
    });
  },

  update: async (id: number, data: UpdateBillingAddressRequest) => {
    return ApiClient.put(`/billing/contact/${id}`, data, billingAddressSchema, {
      mock: () => {
        const { address, collection } = findMockAddress(id);
        const idx = collection.indexOf(address);
        collection[idx] = { ...address, ...data };
        return { ...collection[idx] };
      },
    });
  },

  delete: async (id: number) => {
    return ApiClient.delete(`/billing/contact/${id}`, undefined, undefined, {
      mock: () => {
        const { address, collection } = findMockAddress(id);
        const idx = collection.indexOf(address);
        collection.splice(idx, 1);
      },
    });
  },
};
